import axios from 'axios'

import localStorageManager from '@/localStorageManager'

import {
  getSynonymNames,
} from '@/helpers/synonyms'

const RESET = 'RESET'
const SET_SIDE_MENU_GROUPING = 'SET_SIDE_MENU_GROUPING'
const SET_SIDE_MENU_SETTINGS = 'SET_SIDE_MENU_SETTINGS'
const SET_RECENT_LOCATIONS = 'SET_RECENT_LOCATIONS'

const STATION_HISTORY = 'locationHistory'

const MAX_RECENT_LOCATIONS = 5

import {
  SOURCE_TYPES,
  SOURCE_TYPES_WITH_NAME_INDEX,
} from '@/store/MonitoringFeature/monitoringfeature'

const ITEM_TYPES = {
  monitoringFeature: 'Location',
  station: 'Station',
}

export const groupTypes = {
  stationCategory: {
    name: 'By station category',
    value: 'station_category',
  },
  sampleMediaType: {
    name: 'By sample media type',
    value: 'data_media_type',
  },
  timeSeriesTag: {
    name: 'By time series tag',
    value: 'time_series_tag',
  },
  vegetativeCommunityType: {
    name: 'By plot community type',
    value: 'vegetative_community_type',
  },
  vegetativePlotType: {
    name: 'By plot type',
    value: 'vegetative_plot_type',
  },
  surveyType: {
    name: 'By survey type',
    value: 'survey_type',
  },
}

export const sourceGroupTypes = {
  [SOURCE_TYPES.discreteEnvirodata]: [
    groupTypes.stationCategory,
    groupTypes.sampleMediaType,
  ],
  [SOURCE_TYPES.continuousAquarius]: [
    groupTypes.stationCategory,
    groupTypes.timeSeriesTag,
  ],
  [SOURCE_TYPES.vegetativeMonitoringEnvirodata]: [
    groupTypes.vegetativeCommunityType,
    groupTypes.vegetativePlotType,
  ],
  [SOURCE_TYPES.surveysEnvirodata]: [
    groupTypes.surveyType,    
  ],
}

let initialState = () => ({
  sideMenuGrouping: {},
  sideMenuSettings: {},
  recentLocationIds: [],
})

export default {
  namespaced: true,
  state: initialState,
  actions: {
    async saveSideMenuSetting(_, {
      settings,
    }) {
      try {
        let response = await axios.post('api/UserGroupSetting/Update', settings)
        return response.data
      } catch (error) {
        console.error('Failed to save side menu settings', error)
        return error.response.data
      }
    },
    async fetchSideMenuSettings(_, {
      tenantId,
    }) {
      try {
        let response = await axios.get(`api/UserGroupSetting/UserGroup/${tenantId}`)
        return response.data
      } catch (error) {
        console.error('Failed to fetch side menu settings', error)
        return error.response.data
      }
    },
    async fetchSideMenu({
      commit,
      rootGetters,
    }, {
      tenantId = null,
    } = {}) {
      if (!tenantId) {
        tenantId = rootGetters['authenticate/currentTenant'].id
      }

      let response = await axios.post('api/SideMenuLoading', {
        tenantId,
      })

      let data = response.data
      let grouping = data.Grouping

      let settings = {}
      data.UserGroupSettings.forEach(setting => settings[setting.SettingType] = setting)

      commit(SET_SIDE_MENU_SETTINGS, settings)
      commit(SET_SIDE_MENU_GROUPING, grouping)
    },
    fetchRecentLocations({
      commit,
      rootGetters,
    }) {
      const currentUser = rootGetters['authenticate/currentUser']
      const username = currentUser && currentUser.username

      let locationHistory = JSON.parse(localStorageManager.getItem(STATION_HISTORY))

      if (locationHistory === null) {
        locationHistory = [{
          username,
          locations: [],
        }]
      }

      let userLocationHistory = locationHistory.find(x => x.username == username) || {}
      let recentLocations = userLocationHistory.locations || []

      commit(SET_RECENT_LOCATIONS, recentLocations)
      return {
        locationHistory,
        userLocationHistory,
        recentLocations,
      }
    },
    async saveToRecentLocations({
      commit,
      dispatch,
    }, {
      monitoringFeatureId,
    }) {
      if (!monitoringFeatureId) return

      let {
        locationHistory,
        userLocationHistory,
        recentLocations,
      } = await dispatch('fetchRecentLocations')

      let indexFound = recentLocations.indexOf(monitoringFeatureId)
      if (indexFound > -1) {
        recentLocations.splice(indexFound, 1)
      }

      recentLocations.unshift(monitoringFeatureId)
      recentLocations = recentLocations.slice(0, MAX_RECENT_LOCATIONS)

      userLocationHistory.locations = recentLocations
      localStorageManager.setItem(STATION_HISTORY, JSON.stringify(locationHistory))

      commit(SET_RECENT_LOCATIONS, recentLocations)
    },
  },
  mutations: {
    [SET_SIDE_MENU_GROUPING](state, sideMenuGrouping) {
      state.sideMenuGrouping = sideMenuGrouping
    },
    [SET_SIDE_MENU_SETTINGS](state, sideMenuSettings) {
      state.sideMenuSettings = sideMenuSettings
    },
    [SET_RECENT_LOCATIONS](state, recentLocations) {
      state.recentLocationIds = recentLocations
    },
    [RESET](state) {
      state.configurations = initialState.configurations
    },
  },
  getters: {
    sideMenuGrouping: (state) => state.sideMenuGrouping,
    sideMenuSettings: (state) => state.sideMenuSettings,
    recentLocationIds: (state) => state.recentLocationIds,
    searchableLocationItems: (state, getters, rootState, rootGetters) => {
      let mfIndex = {}

      const monitoringFeatures = rootGetters['monitoringfeature/monitoringFeatures']
      const getDisplayName = rootGetters['monitoringfeature/getDisplayName']
      const dataSources = rootGetters['datasource/dataSources']

      monitoringFeatures.forEach(mf => {
        let mfItem = {
          primaryName: getDisplayName(mf) || '',
          allNames: [],
          dataTypes: [],
          dataTypesIndex: {},
          type: ITEM_TYPES.monitoringFeature,
          id: mf.Id,
          monitoringFeature: mf,
        }

        mfIndex[mfItem.id] = mfItem
      })

      Object.entries(dataSources).forEach(([sourceType, stations]) => {
        stations.forEach(station => {
          let names = []
          let synonyms = getSynonymNames(sourceType, station)

          names.push(...synonyms)

          let mfItem = mfIndex[station.MonitoringFeatureId]

          if (!mfItem) return
          mfItem.allNames.push(...names)
          mfItem.dataTypes.push({
            station,
            name: station.LocationIdentifier,
            sourceType: sourceType,
            type: SOURCE_TYPES_WITH_NAME_INDEX[sourceType].dataType,
          })

          mfItem.dataTypesIndex[sourceType] = station
        })
      })

      return Object.values(mfIndex)
    },
    searchableLocationItemsIndex: (state, getters) => {
      let index = {}
      getters.searchableLocationItems.forEach(sli => index[sli.id] = sli)
      return index
    },
    searchableLocationItemsIndexByType: (state, getters) => {
      let index = {}
      Object.values(SOURCE_TYPES).forEach(sourceType => {
        let sourceTypeItems = getters.searchableLocationItems
          .filter(item => item.dataTypes
            .find(dataType => dataType.sourceType == sourceType))

        index[sourceType] = sourceTypeItems
      })
      return index
    },
  },
}